import Slider from 'components/commercetools-ui/atoms/slider';
import Subtitle, { SubtitleProps } from 'components/commercetools-ui/atoms/subtitle';
import Typography from 'components/commercetools-ui/atoms/typography';
import Wrapper from 'components/HOC/wrapper';
import useClassNames from 'helpers/hooks/useClassNames';
import useTouchDevice from 'helpers/hooks/useTouchDevice';
import { mediumDesktop, mobile, tablet } from 'helpers/utils/screensizes';
import { FC } from 'react';
import { Product } from 'shared/types/product/Product';
import { SwiperOptions } from 'swiper';
import { Reference } from 'types/reference';
import Link from '../../content/link';
import Tile from '../product-tile';
import useTrack from './useTrack';

export interface ProductSliderProps extends Partial<SwiperOptions> {
  products: Product[];
  title: string;
  titleVariant?: 'xs' | 'sm' | 'lg';
  subline?: string;
  subtitleVariant?: SubtitleProps['variant'];
  ctaLabel?: string;
  ctaLink?: Reference;
  clearDefaultWrapperStyles?: boolean;
  slidesPerView?: number;
  disableProductQuickView?: boolean;
  disableProductWishlistButton?: boolean;
  disableProductVariants?: boolean;
  innerArrows?: boolean;
  solidArrows?: boolean;
  onProductClick?: (product: Product) => void;
  classNames?: {
    title?: string;
  };
  variant?: 'normal' | 'cart';
}

const ProductSlider: FC<ProductSliderProps> = ({
  products,
  title,
  subline,
  ctaLabel,
  ctaLink,
  onProductClick,
  titleVariant = 'lg',
  subtitleVariant = 'lg',
  clearDefaultWrapperStyles = false,
  disableProductQuickView = false,
  disableProductWishlistButton = false,
  disableProductVariants = true,
  innerArrows = true,
  solidArrows = true,
  breakpoints = {},
  classNames = {},
  ...props
}) => {
  const { isTouchDevice } = useTouchDevice();

  const { trackClick } = useTrack();

  const titleClassName = useClassNames([
    'mb-12',
    classNames.title,
    {
      xs: 'text-lg font-medium',
      sm: 'text-xl font-medium',
      lg: 'text-2xl font-medium',
    }[titleVariant ?? 'lg'],
  ]);
  return (
    <Wrapper
      className="py-32"
      background="neutral-200"
      clearDefaultStyles={clearDefaultWrapperStyles}
      data-id="product-slider-wrapper"
    >
      <div data-id="product-slider-header">
        <Typography className={titleClassName} as="h3" data-id="product-slider-title">
          {title}
        </Typography>
        {(subline || ctaLink) && (
          <div
            className="mt-8 flex items-center justify-between md:mt-16 lg:mt-14"
            data-id="product-slider-subline-cta"
          >
            {subline && <Subtitle subtitle={subline} variant={subtitleVariant} data-id="product-slider-subtitle" />}
            {ctaLink && (
              <div className="hidden lg:block" data-id="product-slider-cta-desktop">
                <Link target={ctaLink} withArrow data-id="product-slider-cta-link-desktop">
                  <span
                    className="font-medium leading-[24px] text-secondary-black"
                    data-id="product-slider-cta-label-desktop"
                  >
                    {ctaLabel}
                  </span>
                </Link>
              </div>
            )}
          </div>
        )}
        {ctaLink && (
          <div className="mt-20 block md:mt-16 lg:hidden" data-id="product-slider-cta-mobile">
            <Link target={ctaLink} withArrow data-id="product-slider-cta-link-mobile">
              <span
                className="font-medium leading-[24px] text-secondary-black"
                data-id="product-slider-cta-label-mobile"
              >
                {ctaLabel}
              </span>
            </Link>
          </div>
        )}
      </div>
      <div className="mt-20 md:mt-24 lg:mt-20" data-id="product-slider-content">
        <div className="relative mt-6 w-full" data-id="product-slider-slider-wrapper">
          <Slider
            slidesPerView={1.2}
            slidesPerGroup={1}
            dots={false}
            arrows
            innerArrows={innerArrows}
            solidArrows={solidArrows}
            nextButtonStyles={{ transform: 'translateY(-400%)' }}
            prevButtonStyles={{ transform: 'translateY(-400%)' }}
            allowTouchMove
            allowArrowsOnTouchDevice
            spaceBetween={8}
            breakpoints={{
              [mobile]: {
                slidesPerView: 1.1,
                spaceBetween: 25,
                ...(breakpoints[tablet] ?? {}),
              },
              [tablet]: {
                slidesPerView: 2.3,
                spaceBetween: 25,
                ...(breakpoints[tablet] ?? {}),
              },
              [mediumDesktop]: {
                slidesPerView: 4,
                slidesPerGroup: 4,
                spaceBetween: 25,
                ...(breakpoints[mediumDesktop] ?? {}),
              },
            }}
            {...props}
            data-id="product-slider"
          >
            {products.map((product, index) => (
              <Tile
                key={product.productId}
                disableQuickView={disableProductQuickView}
                disableWishlistButton={disableProductWishlistButton}
                disableVariants={disableProductVariants}
                product={product}
                onClick={() => {
                  trackClick(product, index + 1);
                  onProductClick?.(product);
                }}
                dataId={`product-slider-tile-${index}`}
              />
            ))}
          </Slider>
        </div>
      </div>
    </Wrapper>
  );
};

export default ProductSlider;
